<template>
  <basic-dialog :show="show" @close="$emit('close')" max-width="1200">
    <template v-slot:title>{{ editorTitle }}</template>
    <v-row>
      <v-col>
        <v-combobox
          v-model="name"
          :items="templateNames"
          label="Name"
          :outlined="outlinedInputs"
          hide-details
          @change="onNameChange"
        ></v-combobox>
      </v-col>
      <v-col>
        <v-text-field
          v-model="label"
          label="Label"
          :outlined="outlinedInputs"
          hide-details
        ></v-text-field>
      </v-col>
      <v-col>
        <v-select
          v-model="level"
          :items="thirdPartyLevels"
          label="Level"
          :outlined="outlinedInputs"
          hide-details
          item-text="name"
          item-value="value"
        ></v-select>
      </v-col>
      <v-col cols="2">
        <v-switch
          v-model="inUse"
          label="In Use"
          :outlined="outlinedInputs"
          hide-details
        ></v-switch>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-container fluid>
      <v-row align="center" class="pb-2">
        <h1 class="title">Settings</h1>
        <v-spacer></v-spacer>
        <v-btn
          v-if="missingTemplateSettings.length > 0"
          text
          small
          color="primary"
          class="mr-2"
          @click="addMissingTemplateSettings"
          >Add missing settings ({{ missingTemplateSettings.length }})</v-btn
        >
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              fab
              dark
              color="primary"
              @click="onCreateNewSetting"
              small
            >
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Add New Setting</span>
        </v-tooltip>
      </v-row>
      <v-row>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="settings"
            :items-per-page="15"
            class="elevation-1"
          >
            <template v-slot:item.content="{ item }">
              {{ displayContent(item) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <default-list-actions
                :item="item"
                hideView
                @editItem="onEditItem"
                @deleteItem="onDeleteItem"
              ></default-list-actions>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <third-party-setting-edit-dialog
        :setup-name="name"
        :show="showEditDialog"
        @close="showEditDialog = false"
        v-model="editedItem"
        @input="onSaveSetting"
      />
    </v-container>
    <template v-slot:actions>
      <v-btn color="primary" @click="onSave" :disabled="!isValid">Save</v-btn>
    </template>
    <v-row v-if="debug">
      <v-col>
        <pre>{{ settings }}</pre>
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import { removeGQLTypename } from "../../../../helpers";
import BasicDialog from "../../shared/BasicDialog.vue";
import DefaultListActions from "../../shared/DefaultListActions.vue";
import ThirdPartySettingEditDialog from "./ThirdPartySettingEditDialog.vue";
import {
  THIRD_PARTY_SETUP_TEMPLATES,
  thirdPartySetupTemplate,
  thirdPartySettingDefinition,
  isTrueSetting,
} from "../../../../constants/thirdPartySetups";
const randomize = require("randomatic");
export default {
  name: "third-party-setup-edit-dialog",

  props: {
    show: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => null,
    },
  },

  components: { BasicDialog, DefaultListActions, ThirdPartySettingEditDialog },

  data() {
    return {
      debug: false,
      name: this.value ? this.value.name : "",
      label: this.value ? this.value.label : "",
      level: this.value ? this.value.level : "OWNER",
      inUse: this.value ? this.value.inUse : true,
      thirdPartyLevels: [
        {
          name: "Owner",
          value: "OWNER",
        },
        {
          name: "Agent",
          value: "AGENT",
        },
        {
          name: "Staff",
          value: "STAFF",
        },
      ],
      settings:
        this.value && this.value.settings && this.value.settings.edges
          ? this.value.settings.edges
              .map(removeGQLTypename)
              .map(this.mapSetting)
          : [],
      editedItem: null,
      showEditDialog: false,
      headers: [
        { text: "Name", value: "name" },
        { text: "Content", value: "content" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },

  computed: {
    editorTitle() {
      return this.value ? "Edit Third Party Setup" : "Create Third Party Setup";
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    isValid() {
      return (
        (this.name || "").trim() != "" &&
        this.level != "" &&
        this.settings.length > 0
      );
    },
    templateNames() {
      return THIRD_PARTY_SETUP_TEMPLATES.map((t) => t.name);
    },
    // known settings of this setup (#11460) that it doesn't have yet
    missingTemplateSettings() {
      const template = thirdPartySetupTemplate(this.name);
      if (!template) return [];
      return template.settings.filter(
        (t) => !this.settings.some((s) => (s.name || "").trim() == t.name)
      );
    },
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.name = newVal.name;
        this.label = newVal.label;
        this.level = newVal.level;
        this.inUse = newVal.inUse;
        this.settings = newVal.settings.edges
          ? newVal.settings.edges.map(removeGQLTypename).map(this.mapSetting)
          : [];
      } else {
        this.name = "";
        this.label = "";
        this.level = "OWNER";
        this.inUse = true;
        this.settings = [];
      }
    },
  },

  methods: {
    displayContent(item) {
      const definition = thirdPartySettingDefinition(this.name, item.name);
      if (definition.type == "password") return item.content ? "••••••••" : "";
      if (definition.type == "boolean") {
        const content = (item.content || "").trim();
        return isTrueSetting(content || definition.default) ? "On" : "Off";
      }
      return item.content;
    },
    // Picking a known setup on a new one fills in its label, level and settings
    onNameChange() {
      const template = thirdPartySetupTemplate(this.name);
      if (!template || this.value) return;
      if (!this.label) this.label = template.label;
      this.level = template.level;
      this.addMissingTemplateSettings();
    },
    addMissingTemplateSettings() {
      this.missingTemplateSettings.forEach((t) => {
        this.settings.push({
          id: randomize("Aa0", 10),
          name: t.name,
          content: t.default || "",
        });
      });
    },
    mapSetting(setting) {
      return {
        id: setting.id,
        name: setting.name,
        content: setting.content,
      };
    },
    onCreateNewSetting() {
      this.editedItem = null;
      this.showEditDialog = true;
    },
    onSaveSetting(payload) {
      const index = this.settings.findIndex(
        (setting) => setting.id === payload.id
      );
      if (index != -1) {
        this.settings.splice(index, 1, payload);
      } else {
        this.settings.push(payload);
      }
      this.showEditDialog = false;
    },
    onEditItem(item) {
      this.editedItem = item;
      this.showEditDialog = true;
    },
    onDeleteItem(item) {
      const index = this.settings.findIndex(
        (setting) => setting.name == item.name
      );
      this.settings.splice(index, 1);
    },
    onSave() {
      this.$emit("input", {
        id: this.value ? this.value.id : undefined,
        name: (this.name || "").trim(),
        label: this.label,
        level: this.level,
        inUse: this.inUse,
        settings: this.settings.map((s) => {
          return {
            name: s.name,
            content: s.content,
          };
        }),
      });
    },
  },
};
</script>

<style>
</style>


// WEBPACK FOOTER //
// src/components/gt/plugins/third-party-setups/ThirdPartySetupEditDialog.vue